/**
 * KAN-852: взять клиента в работу в приложении менеджера (/m).
 * Действия по роли решает workActions (через useAttentionDone(run, 'app')),
 * права на конкретного клиента clientManagerRights: обычный менеджер берёт
 * ничьего и своего, у чужого только подпись "у <ФИО>"; старший берёт любого
 * себе и назначает другого; владелец и оператор назначают.
 * Взять = POST dashboard/attention/<id>/done/ (тот же путь, что в кабинете):
 * строка уходит из внимания, клиент закрепляется, бот больше не пишет первым.
 * KAN-869: обычный менеджер на своём клиенте рядом с "Взять в работу" видит
 * "Передать" (canHandOffClient): то же окно выбора в режиме передачи (без
 * себя), сохранение PATCH clients/<id>/ manager_id (useAssignClientManager),
 * а не attention done, где backend manager_id обычного менеджера не читает.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import type { AttentionDoneDialogMode } from '@/components/dashboard/AttentionDoneDialog';
import { attentionActionErrorMessage } from '@/components/dashboard/attentionActionError';
import { attentionRowRemover, useAttentionActions } from '@/components/dashboard/useAttentionActions';
import { useAttentionDone } from '@/components/dashboard/useAttentionDone';
import { useClientManagerRights } from '@/hooks/useClientManagerRights';
import { useAssignClientManager } from '@/hooks/useManagers';
import { MOBILE_ATTENTION_QUERY_KEY } from '@/hooks/useMobileData';
import { canHandOffClient, HAND_OFF_LABEL, handedOffText } from '@/lib/clientManagerRights';
import { clientOwnerLabel } from '@/lib/mobileClient';
import { ASSIGN_SHORT_LABEL, ASSIGN_TO_WORK_LABEL } from '@/lib/workActions';
import type { AttentionItem } from '@/services/api/dashboard';
import type { ManagerBrief } from '@/types/manager';

export type AttentionDone = ReturnType<typeof useAttentionDone>;

export interface MobileWork {
  /** "ничей" / "ваш" / "у <ФИО>". */
  ownerLabel: string;
  /** Показывать "Взять в работу". */
  canTake: boolean;
  /** Показывать "Назначить" (окно выбора менеджера). */
  canAssign: boolean;
  /** "Назначить" рядом с "Взять в работу", иначе "Назначить в работу". */
  assignLabel: string;
  take: () => void;
  assign: () => void;
  /** KAN-869: идёт сохранение передачи клиента. */
  busy: boolean;
  /** Пропсы окна выбора менеджера (назначение или передача). */
  dialog: MobileWorkDialog;
}

export interface MobileWorkDialog {
  item: AttentionItem | null;
  mode: AttentionDoneDialogMode;
  defaultManager: ManagerBrief | null;
  excludeIds?: readonly string[];
  onConfirm: (managerId: string | null, manager: ManagerBrief | null) => void;
  onCancel: () => void;
}

/** Подпись "чей клиент" для текущего пользователя. */
export function useClientOwnerLabel(manager: ManagerBrief | null | undefined): string {
  const { selfId } = useClientManagerRights(manager?.id);
  return clientOwnerLabel(manager, selfId);
}

/** Действия взятия в работу для строки item поверх готового useAttentionDone(run, 'app'). */
export function useMobileWork(item: AttentionItem, done: AttentionDone): MobileWork {
  const rights = useClientManagerRights(item.manager?.id);
  const handOff = canHandOffClient(rights, item.manager?.id);
  const assignment = useAssignClientManager(item.client_id);
  // Строка, для которой открыто окно "Передать"; null: окно закрыто.
  const [handOffItem, setHandOffItem] = useState<AttentionItem | null>(null);

  const confirmHandOff = (managerId: string | null, manager: ManagerBrief | null) => {
    setHandOffItem(null);
    if (!managerId) return;
    assignment.mutate(managerId, {
      onSuccess: () => toast.success(handedOffText(manager?.full_name)),
      onError: (error) => toast.error(attentionActionErrorMessage(error)),
    });
  };

  const dialog: MobileWorkDialog = handOffItem
    ? {
        item: handOffItem,
        mode: 'handoff',
        defaultManager: null,
        excludeIds: rights.selfId ? [rights.selfId] : undefined,
        onConfirm: confirmHandOff,
        onCancel: () => setHandOffItem(null),
      }
    : {
        item: done.dialogItem,
        mode: 'assign',
        defaultManager: done.defaultManager,
        onConfirm: (managerId) => done.confirm(managerId),
        onCancel: done.cancel,
      };

  return {
    ownerLabel: clientOwnerLabel(item.manager, rights.selfId),
    canTake: rights.canChange && done.work.takeSelf,
    canAssign: handOff || (rights.canChange && done.work.assign),
    assignLabel: handOff ? HAND_OFF_LABEL : done.work.takeSelf ? ASSIGN_SHORT_LABEL : ASSIGN_TO_WORK_LABEL,
    take: () => done.take(item),
    assign: () => (handOff ? setHandOffItem(item) : done.openAssign(item)),
    busy: assignment.isPending,
    dialog,
  };
}

/** Карточка вкладки "Внимание": свои запрос и окно назначения. */
export function useMobileAttentionWork(item: AttentionItem) {
  const actions = useAttentionActions(MOBILE_ATTENTION_QUERY_KEY, attentionRowRemover);
  const done = useAttentionDone(actions.run, 'app');
  const work = useMobileWork(item, done);
  return { ...work, done, pending: actions.isPending(item.client_id) || work.busy };
}
